// Community Maps section — two rows (popular + new), mode filter, map code input

import type { MapInfo } from "../../net/server.js";
import { listMaps, getMap } from "../../engine/launcher.js";
import { FONT, PANEL_BG, BORDER, TEXT, TEXT_DIM, ACCENT, INPUT_BG } from "./theme.js";

const DIFF_LABELS = ["Easy", "Medium", "Hard"];
function difficultyLabel(d: [number, number, number]): string | null {
  const total = d[0] + d[1] + d[2];
  if (total < 3) return null; // not enough votes
  let max = 0, idx = 0;
  for (let i = 0; i < 3; i++) { if (d[i] > max) { max = d[i]; idx = i; } }
  return DIFF_LABELS[idx];
}

export type MapSectionCallbacks = {
  onHostMap: (code: string) => void;
  onCreateMap: () => void;
};

const MODES = ["surf", "bhop", "freeplay"];
const CARDS_PER_ROW = 5;

export class MapSection {
  private el: HTMLDivElement;
  private rowsEl: HTMLDivElement;
  private detailEl: HTMLDivElement;
  private relayHttp: string;
  private callbacks: MapSectionCallbacks;
  private modeFilter: string | undefined;
  private filterButtons = new Map<string, HTMLButtonElement>();

  constructor(relayHttp: string, callbacks: MapSectionCallbacks) {
    this.relayHttp = relayHttp;
    this.callbacks = callbacks;

    this.el = document.createElement("div");
    this.el.style.cssText = `width:780px;margin-top:24px;`;

    // Header row
    const header = document.createElement("div");
    header.style.cssText = `display:flex;align-items:center;gap:12px;margin-bottom:10px;`;

    const title = document.createElement("div");
    title.textContent = "COMMUNITY MAPS";
    title.style.cssText = `font:bold 14px ${FONT};color:${TEXT};letter-spacing:2px;`;
    header.appendChild(title);

    // Mode filter buttons
    const filterBar = document.createElement("div");
    filterBar.style.cssText = `display:flex;gap:4px;`;

    const addFilter = (label: string, mode: string | undefined) => {
      const btn = document.createElement("button");
      btn.textContent = label;
      btn.style.cssText =
        `padding:3px 10px;border:1px solid ${BORDER};cursor:pointer;` +
        `font:10px ${FONT};letter-spacing:1px;background:transparent;color:${TEXT_DIM};`;
      btn.addEventListener("click", () => {
        this.modeFilter = mode;
        this.updateFilterStyles();
        this.refresh();
      });
      filterBar.appendChild(btn);
      this.filterButtons.set(label, btn);
    };

    addFilter("All", undefined);
    for (const m of MODES) addFilter(m.charAt(0).toUpperCase() + m.slice(1), m);
    header.appendChild(filterBar);

    // Map code input
    const codeInput = document.createElement("input");
    codeInput.placeholder = "Map code...";
    codeInput.style.cssText =
      `padding:4px 8px;background:${INPUT_BG};color:${TEXT};border:1px solid ${BORDER};` +
      `font:11px ${FONT};outline:none;width:120px;margin-left:auto;`;
    codeInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && codeInput.value.trim()) {
        this.showMapByCode(codeInput.value.trim());
        codeInput.value = "";
      }
    });
    header.appendChild(codeInput);

    const createBtn = document.createElement("button");
    createBtn.textContent = "CREATE A MAP";
    createBtn.style.cssText =
      `padding:5px 14px;background:transparent;color:${TEXT_DIM};border:1px solid ${BORDER};` +
      `cursor:pointer;font:bold 10px ${FONT};letter-spacing:1px;`;
    createBtn.addEventListener("click", () => callbacks.onCreateMap());
    header.appendChild(createBtn);

    this.el.appendChild(header);

    // Detail popup (above rows, hidden by default)
    this.detailEl = document.createElement("div");
    this.detailEl.style.cssText = `display:none;margin:10px 0;`;
    this.el.appendChild(this.detailEl);

    // Rows container
    this.rowsEl = document.createElement("div");
    this.el.appendChild(this.rowsEl);
  }

  mount(parent: HTMLElement): void {
    parent.appendChild(this.el);
    this.updateFilterStyles();
    this.refresh();
  }

  unmount(): void {
    this.el.remove();
  }

  private updateFilterStyles(): void {
    for (const [label, btn] of this.filterButtons) {
      const isAll = label === "All";
      const active = isAll ? !this.modeFilter : this.modeFilter === label.toLowerCase();
      btn.style.background = active ? ACCENT : "transparent";
      btn.style.color = active ? "#fff" : TEXT_DIM;
      btn.style.borderColor = active ? ACCENT : BORDER;
    }
  }

  private async refresh(): Promise<void> {
    try {
      const [popular, recent] = await Promise.all([
        listMaps(this.relayHttp, "popular", this.modeFilter, CARDS_PER_ROW),
        listMaps(this.relayHttp, "new", this.modeFilter, CARDS_PER_ROW),
      ]);

      this.rowsEl.innerHTML = "";

      const totalMaps = new Set([...popular.map(m => m.code), ...recent.map(m => m.code)]).size;

      if (totalMaps === 0) {
        const empty = document.createElement("div");
        empty.textContent = this.modeFilter
          ? `No ${this.modeFilter} maps yet — be the first to publish!`
          : "No community maps yet — be the first to publish!";
        empty.style.cssText = `color:${TEXT_DIM};font:11px ${FONT};padding:16px 0;`;
        this.rowsEl.appendChild(empty);
        return;
      }

      // If few maps, show single row
      if (totalMaps <= CARDS_PER_ROW) {
        this.renderRow(popular.length > 0 ? popular : recent);
        return;
      }

      // Two rows
      if (popular.length > 0) this.renderRow(popular, "POPULAR");
      if (recent.length > 0) this.renderRow(recent, "RECENTLY PUBLISHED");
    } catch {
      this.rowsEl.innerHTML = "";
      const err = document.createElement("div");
      err.textContent = "Could not load community maps";
      err.style.cssText = `color:${TEXT_DIM};font:11px ${FONT};padding:16px 0;`;
      this.rowsEl.appendChild(err);
    }
  }

  private renderRow(maps: MapInfo[], label?: string): void {
    if (label) {
      const labelEl = document.createElement("div");
      labelEl.textContent = label;
      labelEl.style.cssText = `font:bold 10px ${FONT};color:${TEXT_DIM};letter-spacing:1px;margin:12px 0 6px;`;
      this.rowsEl.appendChild(labelEl);
    }

    const grid = document.createElement("div");
    grid.style.cssText = `display:grid;grid-template-columns:repeat(${CARDS_PER_ROW},1fr);gap:10px;`;

    for (const map of maps) {
      const card = document.createElement("div");
      card.style.cssText =
        `background:${PANEL_BG};border:1px solid ${BORDER};` +
        `cursor:pointer;transition:border-color 0.15s;overflow:hidden;`;
      card.addEventListener("mouseenter", () => { card.style.borderColor = ACCENT; });
      card.addEventListener("mouseleave", () => { card.style.borderColor = BORDER; });
      card.addEventListener("click", () => this.showDetail(map));

      // Thumbnail
      if (map.thumbnail) {
        const img = document.createElement("img");
        img.src = map.thumbnail;
        img.style.cssText = "width:100%;height:80px;object-fit:cover;display:block;";
        card.appendChild(img);
      } else {
        const placeholder = document.createElement("div");
        placeholder.style.cssText = `width:100%;height:80px;background:#15171c;`;
        card.appendChild(placeholder);
      }

      // Info
      const info = document.createElement("div");
      info.style.cssText = `padding:6px 8px;`;

      const name = document.createElement("div");
      name.textContent = map.name;
      name.style.cssText = `font:bold 11px ${FONT};color:${TEXT};overflow:hidden;text-overflow:ellipsis;white-space:nowrap;`;
      info.appendChild(name);

      const meta = document.createElement("div");
      meta.style.cssText = `font:10px ${FONT};color:${TEXT_DIM};margin-top:2px;display:flex;gap:6px;`;

      const modeSpan = document.createElement("span");
      modeSpan.textContent = map.mode;
      meta.appendChild(modeSpan);

      const playsSpan = document.createElement("span");
      playsSpan.textContent = `${map.plays} plays`;
      meta.appendChild(playsSpan);

      const diff = difficultyLabel(map.difficulty);
      if (diff) {
        const diffSpan = document.createElement("span");
        diffSpan.textContent = diff;
        meta.appendChild(diffSpan);
      }

      info.appendChild(meta);
      card.appendChild(info);
      grid.appendChild(card);
    }

    this.rowsEl.appendChild(grid);
  }

  async showMapByCode(code: string): Promise<void> {
    try {
      const map = await getMap(this.relayHttp, code);
      this.showDetail(map);
    } catch {
      this.detailEl.style.display = "block";
      this.detailEl.innerHTML = "";
      const errDiv = document.createElement("div");
      errDiv.style.cssText = `background:${PANEL_BG};border:1px solid ${BORDER};padding:12px;font:11px ${FONT};color:#e55;`;
      errDiv.textContent = `Map not found: ${code}`;
      this.detailEl.appendChild(errDiv);
    }
  }

  private showDetail(map: MapInfo): void {
    this.detailEl.style.display = "block";
    this.detailEl.innerHTML = "";

    const panel = document.createElement("div");
    panel.style.cssText =
      `background:${PANEL_BG};border:1px solid ${BORDER};padding:14px;display:flex;gap:14px;`;

    // Left: thumbnail
    if (map.thumbnail) {
      const img = document.createElement("img");
      img.src = map.thumbnail;
      img.style.cssText = "width:180px;height:110px;object-fit:cover;flex-shrink:0;";
      panel.appendChild(img);
    }

    // Right: info
    const info = document.createElement("div");
    info.style.cssText = "flex:1;min-width:0;";

    const nameEl = document.createElement("div");
    nameEl.textContent = map.name;
    nameEl.style.cssText = `font:bold 14px ${FONT};color:${TEXT};margin-bottom:4px;`;
    info.appendChild(nameEl);

    const codeEl = document.createElement("div");
    codeEl.textContent = `${map.code} · ${map.mode}`;
    codeEl.style.cssText = `font:11px ${FONT};color:${ACCENT};margin-bottom:6px;`;
    info.appendChild(codeEl);

    if (map.creatorName) {
      const creator = document.createElement("div");
      creator.textContent = `by ${map.creatorName}`;
      creator.style.cssText = `font:11px ${FONT};color:${TEXT_DIM};margin-bottom:4px;`;
      info.appendChild(creator);
    }

    if (map.description) {
      const desc = document.createElement("div");
      desc.textContent = map.description;
      desc.style.cssText = `font:11px ${FONT};color:${TEXT_DIM};margin-bottom:8px;`;
      info.appendChild(desc);
    }

    // Stats
    const statsRow = document.createElement("div");
    statsRow.style.cssText = `font:11px ${FONT};color:${TEXT_DIM};margin-bottom:10px;display:flex;gap:12px;`;
    const playsSpan = document.createElement("span");
    playsSpan.textContent = `${map.plays} plays`;
    statsRow.appendChild(playsSpan);

    const voteTotal = map.upvotes + map.downvotes;
    if (voteTotal > 0) {
      const voteSpan = document.createElement("span");
      const pct = Math.round(map.upvotes / voteTotal * 100);
      voteSpan.textContent = `${pct}% positive (${voteTotal} votes)`;
      statsRow.appendChild(voteSpan);
    }

    const diff = difficultyLabel(map.difficulty);
    if (diff) {
      const diffSpan = document.createElement("span");
      diffSpan.textContent = `Difficulty: ${diff}`;
      statsRow.appendChild(diffSpan);
    }
    info.appendChild(statsRow);

    // Action buttons
    const btnRow = document.createElement("div");
    btnRow.style.cssText = "display:flex;gap:8px;";

    const hostBtn = document.createElement("button");
    hostBtn.textContent = "HOST WITH THIS MAP";
    hostBtn.style.cssText =
      `padding:6px 14px;background:${ACCENT};color:#fff;border:none;cursor:pointer;` +
      `font:bold 10px ${FONT};letter-spacing:1px;`;
    hostBtn.addEventListener("click", () => {
      this.detailEl.style.display = "none";
      this.callbacks.onHostMap(map.code);
    });
    btnRow.appendChild(hostBtn);

    const closeBtn = document.createElement("button");
    closeBtn.textContent = "CLOSE";
    closeBtn.style.cssText =
      `padding:6px 12px;background:transparent;color:${TEXT_DIM};border:1px solid ${BORDER};` +
      `cursor:pointer;font:10px ${FONT};`;
    closeBtn.addEventListener("click", () => { this.detailEl.style.display = "none"; });
    btnRow.appendChild(closeBtn);

    info.appendChild(btnRow);
    panel.appendChild(info);
    this.detailEl.appendChild(panel);
  }
}
